import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Plus, X } from 'lucide-react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getTenants, saveForwardingConfig, getForwardingConfig } from '../../services/api';
import { toast } from 'react-hot-toast';

type TenantItem = { id: string; name: string; level?: string };

type Cluster = {
  id: string;
  name: string;
  destination: string;
  port: string;
  tenantIds: string[];
};

const ForwardingPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantItem[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [searchAssigned, setSearchAssigned] = useState<string>('');
  const [searchAvailable, setSearchAvailable] = useState<string>('');
  const [draggingTenant, setDraggingTenant] = useState<{ tenantId: string; fromClusterId?: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getTenants({});
        const tenantList: TenantItem[] = (Array.isArray(data) ? data : []).map((t: any) => ({
          id: t.id || t._id,
          name: t.name,
          level: t.level,
        }));
        setTenants(tenantList);

        // Try loading existing config
        try {
          const cfg = await getForwardingConfig();
          if (Array.isArray(cfg?.clusters) && cfg.clusters.length > 0) {
            const loaded = cfg.clusters.map((c: any, idx: number) => ({
              id: c.id || `cluster-${idx + 1}`,
              name: c.name || `Cluster ${idx + 1}`,
              destination: c.destination || '',
              port: c.port || '',
              tenantIds: Array.isArray(c.tenantIds) ? c.tenantIds : [],
            }));
            setClusters(loaded);
          } else {
            // Default Cluster 1 with all tenants selected
            setClusters([
              {
                id: 'cluster-1',
                name: 'Cluster 1',
                destination: '',
                port: '',
                tenantIds: tenantList.map(t => t.id),
              },
            ]);
          }
        } catch (_) {
          // Fallback to default when API unavailable
          setClusters([
            {
              id: 'cluster-1',
              name: 'Cluster 1',
              destination: '',
              port: '',
              tenantIds: tenantList.map(t => t.id),
            },
          ]);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load tenants');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Ensure new clusters default to collapsed
  useEffect(() => {
    setCollapsed(prev => {
      const next = { ...prev };
      clusters.forEach(c => {
        if (next[c.id] === undefined) next[c.id] = true; // default collapsed
      });
      return next;
    });
  }, [clusters]);

  const tenantMap = useMemo(() => {
    const map: Record<string, TenantItem> = {};
    tenants.forEach(t => (map[t.id] = t));
    return map;
  }, [tenants]);

  const assignedTenantIds = useMemo(() => new Set(clusters.flatMap(c => c.tenantIds)), [clusters]);

  const addCluster = () => {
    const newIndex = clusters.length + 1;
    setClusters(prev => [
      ...prev,
      { id: `cluster-${newIndex}`, name: `Cluster ${newIndex}`, destination: '', port: '', tenantIds: [] },
    ]);
  };

  const isValidIPv4 = (value: string) => /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/.test(value.trim());
  const isValidPort = (value: string) => {
    const n = Number(value);
    return Number.isInteger(n) && n >= 1 && n <= 65535;
  };
  const isValidHostname = (value: string) => {
    const v = value.trim().toLowerCase();
    if (!v || v.length > 253) return false;
    const re = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;
    return v.split('.').every(part => re.test(part));
  };

  const handleSave = async () => {
    // Basic validation
    for (const c of clusters) {
      if (!c.destination || !(isValidIPv4(c.destination) || isValidHostname(c.destination))) {
        toast.error(`Invalid destination in ${c.name}. Use IPv4 or hostname`);
        return;
      }
      if (!c.port || !isValidPort(c.port)) {
        toast.error(`Invalid port in ${c.name}`);
        return;
      }
    }
    try {
      setError(null);
      setSaving(true);
      const payload = { clusters };
      await saveForwardingConfig(payload as any);
      toast.success('Forwarding configuration saved');
    } catch (e: any) {
      const msg = e?.message || 'Failed to save configuration';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const removeCluster = (clusterId: string) => {
    // Prevent removing Cluster 1 if it has tenants that are not assigned elsewhere
    const cluster = clusters.find(c => c.id === clusterId);
    if (!cluster) return;

    if (cluster.tenantIds.length > 0) {
      toast.error('Move all tenants from this cluster to another cluster before removing.');
      return;
    }
    setClusters(prev => prev.filter(c => c.id !== clusterId));
  };

  const updateClusterField = (clusterId: string, field: 'name' | 'destination' | 'port', value: string) => {
    setClusters(prev => prev.map(c => (c.id === clusterId ? { ...c, [field]: value } : c)));
  };

  const assignTenant = (clusterId: string, tenantId: string) => {
    setClusters(prev => {
      const next = prev.map(c => ({ ...c, tenantIds: c.tenantIds.filter(id => id !== tenantId) }));
      const idx = next.findIndex(c => c.id === clusterId);
      if (idx >= 0) {
        next[idx].tenantIds = Array.from(new Set([...next[idx].tenantIds, tenantId]));
      }
      return next;
    });
  };

  const unassignTenant = (clusterId: string, tenantId: string) => {
    // If other clusters exist, require assigning to another cluster first
    const otherClusters = clusters.filter(c => c.id !== clusterId);
    const otherExists = otherClusters.length > 0;
    if (otherExists) {
      const assignedElsewhere = otherClusters.some(c => c.tenantIds.includes(tenantId));
      if (!assignedElsewhere) {
        toast.error('Please assign this tenant to another cluster before removing from this one.');
        return;
      }
    }

    setClusters(prev => prev.map(c => (c.id === clusterId ? { ...c, tenantIds: c.tenantIds.filter(id => id !== tenantId) } : c)));
  };

  const findClusterForTenant = (tenantId: string): Cluster | undefined => {
    return clusters.find(c => c.tenantIds.includes(tenantId));
  };

  const availableTenantsFor = (clusterId: string) => {
    // Show all tenants not already in this cluster
    return tenants.filter(t => !clusters.find(c => c.id === clusterId)?.tenantIds.includes(t.id));
  };

  // Drag & Drop helpers
  const onDragStart = (tenantId: string, fromClusterId?: string) => (e: React.DragEvent<HTMLDivElement>) => {
    setDraggingTenant({ tenantId, fromClusterId });
    e.dataTransfer.setData('text/plain', JSON.stringify({ tenantId, fromClusterId }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOverList = (targetId: string) => (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDropTarget(targetId);
    e.dataTransfer.dropEffect = 'move';
  };

  const onDragLeaveList = () => setDropTarget(null);

  const onDropAssignToCluster = (clusterId: string) => (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDropTarget(null);
    try {
      const payload = draggingTenant || JSON.parse(e.dataTransfer.getData('text/plain'));
      if (payload?.tenantId) assignTenant(clusterId, payload.tenantId);
    } catch {}
    setDraggingTenant(null);
  };

  const onDropUnassignFromCluster = (clusterId: string) => (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDropTarget(null);
    try {
      const payload = draggingTenant || JSON.parse(e.dataTransfer.getData('text/plain'));
      if (payload?.tenantId) unassignTenant(clusterId, payload.tenantId);
    } catch {}
    setDraggingTenant(null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="card flex justify-center items-center min-h-[300px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-text-muted-dark">Loading tenants...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 sticky top-0 z-10 bg-background py-3">
        <div>
          <h1 className="text-2xl font-semibold">Forwarding</h1>
          <p className="text-sm text-text-muted-dark mt-1">Configure clusters, destinations and tenant routing</p>
        </div>
        <div className="space-x-2">
          <button className="btn btn-outline" onClick={addCluster}>Add Cluster</button>
          <button className={`btn btn-primary ${saving ? 'opacity-60 pointer-events-none' : ''}`} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {clusters.map((cluster) => (
          <div className="card border border-border-dark/60 bg-background-card/70" key={cluster.id}>
            <div className="card-header flex items-center justify-between gap-4">
              <input
                className="input input-bordered flex-1 bg-background-card text-white placeholder:text-gray-500 border-border-dark focus:border-primary"
                value={cluster.name}
                onChange={e => updateClusterField(cluster.id, 'name', e.target.value)}
              />
              {cluster.id !== 'cluster-1' && (
                <button className="btn btn-ghost btn-sm text-red-400" onClick={() => removeCluster(cluster.id)}>
                  <X className="w-3 h-3 mr-1" /> Remove
                </button>
              )}
            </div>
            <div className="card-body space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">Destination</label>
                  <input
                    className="input input-bordered w-full bg-background-card text-white placeholder:text-gray-500 border-border-dark focus:border-primary"
                    placeholder="e.g. 10.0.0.5 or logger.in.eventussecurity.com"
                    value={cluster.destination}
                    onChange={e => updateClusterField(cluster.id, 'destination', e.target.value)}
                  />
                  <p className="mt-1 text-xs text-text-muted-dark">IPv4 or hostname</p>
                </div>
                <div>
                  <label className="label">Port</label>
                  <input
                    className="input input-bordered w-full bg-background-card text-white placeholder:text-gray-500 border-border-dark focus:border-primary"
                    placeholder="e.g. 514"
                    value={cluster.port}
                    onChange={e => updateClusterField(cluster.id, 'port', e.target.value)}
                  />
                  <p className="mt-1 text-xs text-text-muted-dark">1 - 65535</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <label className="label">Assigned Tenants</label>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{cluster.tenantIds.length} selected</span>
                    </div>
                    <button
                      className="btn btn-ghost btn-xs"
                      aria-label="Toggle section"
                      onClick={() => setCollapsed(prev => ({ ...prev, [cluster.id]: !prev[cluster.id] }))}
                    >
                      {collapsed[cluster.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                    </button>
                  </div>
                  {!collapsed[cluster.id] && (
                  <div className="mb-2 flex items-center gap-2">
                    <input
                      className="input input-bordered w-full bg-background-card text-white placeholder:text-gray-500 border-border-dark focus:border-primary"
                      placeholder="Search assigned tenants..."
                      value={searchAssigned}
                      onChange={e => setSearchAssigned(e.target.value)}
                    />
                    {!!searchAssigned && (
                      <button
                        className="btn btn-xs btn-outline"
                        onClick={() => setSearchAssigned('')}
                        title="Clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  )}
                  {collapsed[cluster.id] ? (
                    <div className="px-3 py-2 text-text-muted-dark text-sm bg-background-card/30 rounded border border-border-dark/60">
                      Section collapsed
                    </div>
                  ) : (
                    <div
                      className={`h-64 overflow-auto border rounded p-2 space-y-2 bg-background-card/60 border-border-dark transition-colors ${dropTarget === `assign-${cluster.id}` ? 'border-primary/60' : ''}`}
                      onDragOver={onDragOverList(`assign-${cluster.id}`)}
                      onDragLeave={onDragLeaveList}
                      onDrop={onDropAssignToCluster(cluster.id)}
                    >
                      {cluster.tenantIds
                        .filter(tid => (tenantMap[tid]?.name || '').toLowerCase().includes(searchAssigned.toLowerCase()))
                        .map(tid => (
                          <div
                            key={tid}
                            className="flex items-center justify-between bg-background-card rounded p-2 border border-transparent hover:border-border-dark/60 transition-colors"
                            draggable
                            onDragStart={onDragStart(tid, cluster.id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-semibold">
                                {(tenantMap[tid]?.name || 'T').charAt(0)}
                              </div>
                              <span>{tenantMap[tid]?.name || tid}</span>
                            </div>
                            <button className="btn btn-xs" onClick={() => unassignTenant(cluster.id, tid)}>
                              <X className="w-3 h-3 mr-1" /> Remove
                            </button>
                          </div>
                        ))}
                      {cluster.tenantIds.length === 0 && (
                        <div className="text-text-muted-dark text-sm">No tenants</div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <label className="label">Available Tenants</label>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-500/10 text-gray-300">{availableTenantsFor(cluster.id).length}</span>
                    </div>
                    <button
                      className="btn btn-ghost btn-xs"
                      aria-label="Toggle section"
                      onClick={() => setCollapsed(prev => ({ ...prev, [cluster.id]: !prev[cluster.id] }))}
                    >
                      {collapsed[cluster.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                    </button>
                  </div>
                  {!collapsed[cluster.id] && (
                  <div className="mb-2 flex items-center gap-2">
                    <input
                      className="input input-bordered w-full bg-background-card text-white placeholder:text-gray-500 border-border-dark focus:border-primary"
                      placeholder="Search available tenants..."
                      value={searchAvailable}
                      onChange={e => setSearchAvailable(e.target.value)}
                    />
                    {!!searchAvailable && (
                      <button
                        className="btn btn-xs btn-outline"
                        onClick={() => setSearchAvailable('')}
                        title="Clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  )}
                  {collapsed[cluster.id] ? (
                    <div className="px-3 py-2 text-text-muted-dark text-sm bg-background-card/30 rounded border border-border-dark/60">
                      Section collapsed
                    </div>
                  ) : (
                    <div
                      className={`h-64 overflow-auto border rounded p-2 space-y-2 bg-background-card/60 border-border-dark transition-colors ${dropTarget === `avail-${cluster.id}` ? 'border-primary/60' : ''}`}
                      onDragOver={onDragOverList(`avail-${cluster.id}`)}
                      onDragLeave={onDragLeaveList}
                      onDrop={onDropUnassignFromCluster(cluster.id)}
                    >
                      {availableTenantsFor(cluster.id)
                        .filter(t => t.name.toLowerCase().includes(searchAvailable.toLowerCase()))
                        .map(t => {
                          const currentCluster = findClusterForTenant(t.id);
                          const isInOther = currentCluster && currentCluster.id !== cluster.id;
                          return (
                            <div
                              key={t.id}
                              className="flex items-center justify-between bg-background-card rounded p-2 border border-transparent hover:border-border-dark/60 transition-colors"
                              draggable
                              onDragStart={onDragStart(t.id, currentCluster?.id)}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-6 h-6 rounded-full ${isInOther ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-500/20 text-gray-300'} flex items-center justify-center text-xs font-semibold`}>
                                  {t.name.charAt(0)}
                                </div>
                                <span>{t.name}</span>
                                {isInOther && (
                                  <span className="text-xs text-text-muted-dark">(in {currentCluster?.name})</span>
                                )}
                              </div>
                              <button className="btn btn-xs btn-primary" onClick={() => assignTenant(cluster.id, t.id)}>
                                {isInOther ? (
                                  <span className="flex items-center"><ArrowRight className="w-3 h-3 mr-1" /> Move here</span>
                                ) : (
                                  <span className="flex items-center"><Plus className="w-3 h-3 mr-1" /> Add</span>
                                )}
                              </button>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ForwardingPage;


